import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import deCommon from './locales/de/common.json';
import enCommon from './locales/en/common.json';
import esCommon from './locales/es/common.json';
import frCommon from './locales/fr/common.json';
import trCommon from './locales/tr/common.json';
import bgCommon from './locales/bg/common.json';
import roCommon from './locales/ro/common.json';
import ruCommon from './locales/ru/common.json';

export const UI_LANGUAGE_STORAGE_KEY = 'ui_language';
export const SUPPORTED_UI_LANGUAGES = ['de', 'en', 'es', 'fr', 'tr', 'bg', 'ro', 'ru'];
const FALLBACK_LANGUAGE = 'de';

const resources = {
  de: { common: deCommon },
  en: { common: enCommon },
  es: { common: esCommon },
  fr: { common: frCommon },
  tr: { common: trCommon },
  bg: { common: bgCommon },
  ro: { common: roCommon },
  ru: { common: ruCommon },
};

function resolveFallbackLanguages(code) {
  const normalized = normalizeUiLanguage(code);
  if (normalized === 'de') {
    return ['de', 'en'];
  }
  if (normalized === 'en') {
    return ['en', 'de'];
  }
  return [normalized, 'de', 'en'];
}

export function normalizeUiLanguage(value) {
  const normalized = String(value || '').trim().toLowerCase();
  return SUPPORTED_UI_LANGUAGES.includes(normalized) ? normalized : FALLBACK_LANGUAGE;
}

export function getStoredUiLanguage() {
  if (typeof localStorage === 'undefined') return FALLBACK_LANGUAGE;
  try {
    return normalizeUiLanguage(localStorage.getItem(UI_LANGUAGE_STORAGE_KEY));
  } catch {
    return FALLBACK_LANGUAGE;
  }
}

export function setStoredUiLanguage(value) {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(UI_LANGUAGE_STORAGE_KEY, normalizeUiLanguage(value));
  } catch {}
}

export function syncLanguageWithUser(user, applyNow = true) {
  const language = normalizeUiLanguage(user?.language);
  setStoredUiLanguage(language);
  if (applyNow && i18n.language !== language) {
    i18n.changeLanguage(language);
  }
  return language;
}

const ERROR_CODE_KEYS = {
  UNAUTHORIZED: 'errors.code.UNAUTHORIZED',
  VALIDATION_ERROR: 'errors.code.VALIDATION_ERROR',
  UNTERNEHMEN_STEUERDATEN_FEHLEN: 'errors.code.UNTERNEHMEN_STEUERDATEN_FEHLEN',
  MISSING_REQUIRED_FIELD: 'errors.code.MISSING_REQUIRED_FIELD',
  RESOURCE_NOT_FOUND: 'errors.code.RESOURCE_NOT_FOUND',
  MINDESTENS_EINE_POSITION_ERFORDERLICH: 'errors.code.MINDESTENS_EINE_POSITION_ERFORDERLICH',
  FORBIDDEN: 'errors.code.FORBIDDEN',
  RECHNUNG_NICHT_EDITIERBAR: 'errors.code.RECHNUNG_NICHT_EDITIERBAR',
  INVALID_STEUERKATEGORIE: 'errors.code.INVALID_STEUERKATEGORIE',
  INVALID_STATUS_TRANSITION: 'errors.code.INVALID_STATUS_TRANSITION',
  INVALID_RABATT_KOMBINATION: 'errors.code.INVALID_RABATT_KOMBINATION',
  INVALID_DATE_RANGE: 'errors.code.INVALID_DATE_RANGE',
  ACCOUNT_READ_ONLY: 'errors.code.ACCOUNT_READ_ONLY',
  ACCOUNT_DELETION_SCHEDULED: 'errors.code.ACCOUNT_DELETION_SCHEDULED',
  ACCOUNT_DELETED: 'errors.code.ACCOUNT_DELETED',
  ACCOUNT_EXPORT_EXPIRED: 'errors.code.ACCOUNT_EXPORT_EXPIRED',
  XML_FILE_REQUIRED: 'errors.code.XML_FILE_REQUIRED',
  XML_INVALID_FILE_TYPE: 'errors.code.XML_INVALID_FILE_TYPE',
  XML_FILE_TOO_LARGE: 'errors.code.XML_FILE_TOO_LARGE',
  XML_UNSUPPORTED_FORMAT: 'errors.code.XML_UNSUPPORTED_FORMAT',
  XML_PARSE_ERROR: 'errors.code.XML_PARSE_ERROR',
  XML_DUPLICATE_UPLOAD: 'errors.code.XML_DUPLICATE_UPLOAD',
  INVALID_INCOMING_INVOICE_STATUS: 'errors.code.INVALID_INCOMING_INVOICE_STATUS',
  DB_MIGRATION_REQUIRED: 'errors.code.DB_MIGRATION_REQUIRED',
  UNKNOWN_ERROR: 'errors.code.UNKNOWN_ERROR',
};

const ERROR_DETAIL_KEYS = {
  'Supabase Auth ist nicht konfiguriert': 'errors.detail.supabaseMissing',
  'Keine gueltige Session aus Supabase erhalten': 'errors.detail.noSession',
  'Ungültige Sprache': 'errors.detail.invalidLanguage',
  'Keine Änderungen angegeben': 'errors.detail.noChanges',
  'Account ist im Read-only-Modus': 'errors.detail.accountReadOnly',
  'Account-Löschung geplant': 'errors.detail.accountDeletionScheduled',
  'Account wurde gelöscht': 'errors.detail.accountDeleted',
  'Kein Löschantrag vorhanden': 'errors.detail.noDeletionRequest',
  'Exportfenster ist abgelaufen': 'errors.detail.accountExportExpired',
};

export function translateApiError(payload) {
  const errorCode = payload?.error?.code;
  if (errorCode && ERROR_CODE_KEYS[errorCode]) {
    return i18n.t(ERROR_CODE_KEYS[errorCode]);
  }

  const rawMessage = payload?.error?.message || payload?.detail || '';
  if (rawMessage && ERROR_DETAIL_KEYS[rawMessage]) {
    return i18n.t(ERROR_DETAIL_KEYS[rawMessage]);
  }
  if (rawMessage) {
    return rawMessage;
  }
  return i18n.t('errors.generic');
}

i18n.use(initReactI18next).init({
  resources,
  lng: getStoredUiLanguage(),
  fallbackLng: resolveFallbackLanguages,
  defaultNS: 'common',
  interpolation: { escapeValue: false },
  returnNull: false,
});

export default i18n;
